import React, { useState, useEffect, useCallback } from 'react';
import { fetchStokBen } from '../services/stokV2Service';
import StokParcaSiparisGecmisiModal from '../components/stok/StokParcaSiparisGecmisiModal';
import StokOzetKart from '../components/stok/StokOzetKart';
import TableListFooter from '../components/TableListFooter';

const PAGE_SIZE = 25;

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const adetStyle = (adet) => {
  if (adet < 0) return { textAlign: 'right', fontWeight: 600, color: 'var(--danger, #e74c3c)' };
  if (adet === 0) return { textAlign: 'right', fontWeight: 600, color: 'var(--text-light, #999)' };
  return { textAlign: 'right', fontWeight: 600, color: 'var(--success, #27ae60)' };
};

const renderKatmanlarAltSatir = (katmanlar) => {
  if (!katmanlar?.length) return null;
  return (
    <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', lineHeight: 1.45 }}>
      {katmanlar.map((k, i) => (
        <span key={k.stokKatmanId || i} style={{ display: 'inline-block', marginRight: '10px' }}>
          {Number(k.birimMaliyet).toFixed(2)} ₺ × {k.toplam}
          {(k.rezerveAdet || 0) > 0 ? ` (${k.rezerveAdet} iade rez.)` : ''}
        </span>
      ))}
    </div>
  );
};

const StokKullanimPage = () => {
  const [stokTab, setStokTab] = useState('guncel');
  const [parcalar, setParcalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aramaQ, setAramaQ] = useState('');
  const [debouncedArama, setDebouncedArama] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [sayfalama, setSayfalama] = useState({ toplam: 0, toplamSayfa: 1 });
  const [gecmisParca, setGecmisParca] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedArama(aramaQ.trim()), 400);
    return () => clearTimeout(t);
  }, [aramaQ]);

  useEffect(() => {
    setSayfa(1);
  }, [stokTab, debouncedArama]);

  const loadParcalar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { sayfa, limit: PAGE_SIZE, tab: stokTab };
      if (debouncedArama) params.q = debouncedArama;
      const response = await fetchStokBen(params);
      if (response?.basarili && response?.veri) {
        setParcalar(response.veri.stokListesi || []);
        setSayfalama(response.veri.sayfalama || { toplam: 0, toplamSayfa: 1 });
      } else {
        setParcalar([]);
        setSayfalama({ toplam: 0, toplamSayfa: 1 });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Stok listesi yüklenemedi'));
      setParcalar([]);
      setSayfalama({ toplam: 0, toplamSayfa: 1 });
    } finally {
      setLoading(false);
    }
  }, [stokTab, debouncedArama, sayfa]);

  useEffect(() => {
    loadParcalar();
  }, [loadParcalar]);

  const emptyMessage = debouncedArama
    ? 'Aramanızla eşleşen parça bulunamadı.'
    : stokTab === 'arsiv'
      ? 'Arşivde parça bulunamadı.'
      : 'Stok kaydı bulunamadı. Geçmiş hareket için parça adı veya kodu ile arayın.';

  return (
    <main className="main-content stok-kullanim-page">
      <div className="content-header">
        <h1>Stok Yönetimi</h1>
      </div>

      {!loading && !debouncedArama && (
        <StokOzetKart parcalar={parcalar} className="stok-ozet-kart--below-header" />
      )}

      <div className="tabs" style={{ marginBottom: '16px' }}>
        <button
          type="button"
          className={`tab-btn ${stokTab === 'guncel' ? 'active' : ''}`}
          onClick={() => setStokTab('guncel')}
        >
          Güncel
        </button>
        <button
          type="button"
          className={`tab-btn ${stokTab === 'arsiv' ? 'active' : ''}`}
          onClick={() => setStokTab('arsiv')}
        >
          Arşiv
        </button>
      </div>

      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <div className="search-box" style={{ maxWidth: '400px' }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Parça adı veya kodu ile ara..."
            value={aramaQ}
            onChange={(e) => setAramaQ(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {loading ? (
        <p>Yükleniyor...</p>
      ) : parcalar.length === 0 ? (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-warehouse" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>{emptyMessage}</div>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Parça adı</th>
                  <th>Parça kodu</th>
                  <th style={{ textAlign: 'right', width: '90px' }}>Öz stok</th>
                  <th style={{ textAlign: 'right', width: '90px' }}>Konsinye</th>
                  <th style={{ textAlign: 'right', width: '90px' }}>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {parcalar.map((p, idx) => (
                  <tr
                    key={p.icerikId || idx}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setGecmisParca({
                        icerikId: p.icerikId,
                        parcaAdi: p.parcaAdi,
                        parcaKodu: p.parcaKodu,
                        mevcutAdet: p.adet ?? 0,
                        ozAdet: p.ozAdet ?? 0,
                      })
                    }
                    title="Arşiv ve sipariş geçmişi için tıklayın"
                  >
                    <td>
                      <div>{p.parcaAdi || '—'}</div>
                      {renderKatmanlarAltSatir(p.katmanlar)}
                    </td>
                    <td>{p.parcaKodu || '—'}</td>
                    <td style={adetStyle(p.ozAdet ?? 0)}>{p.ozAdet ?? 0}</td>
                    <td style={adetStyle(p.konsinyeAdet ?? 0)}>{p.konsinyeAdet ?? 0}</td>
                    <td style={adetStyle(p.adet ?? 0)}>{p.adet ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={sayfalama.toplam ?? 0}
            currentPage={sayfalama.mevcutSayfa || sayfa}
            totalPages={Math.max(1, sayfalama.toplamSayfa || 1)}
            onPageChange={setSayfa}
            loading={loading}
          />
        </>
      )}

      <StokParcaSiparisGecmisiModal
        open={!!gecmisParca}
        onClose={() => setGecmisParca(null)}
        mode="ben"
        icerikId={gecmisParca?.icerikId}
        parcaAdi={gecmisParca?.parcaAdi}
        parcaKodu={gecmisParca?.parcaKodu}
        mevcutAdet={gecmisParca?.mevcutAdet}
        ozAdet={gecmisParca?.ozAdet}
        onStokDegisti={loadParcalar}
      />
    </main>
  );
};

export default StokKullanimPage;
